import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Star,HandCoins, ArrowRight, Briefcase, MapPin, Clock, Filter, GraduationCap, Search, CheckCircle, Send, X, FileText, Sparkles, Gift, Users } from 'lucide-react';
import { featuredAPI, categoryAPI, servicesProductsAPI, jobsAPI, clientDocumentsAPI, trainingsAPI, videosAPI } from '../services/api';
import VideoCourseCard from '../components/VideoCourseCard';
import PremiumClientCard from '../components/PremiumClientCard';
import ServiceProductCard from '../components/ServiceProductCard';
import ScrollingReviews from '../components/ScrollingReviews';
import { toast } from 'sonner';
import LiveStreamSection from '../components/LiveStreamSection';

const loadSafe = (call) =>
  call()
    .catch(() => new Promise((r) => setTimeout(r, 1500)).then(call))
    .catch(() => ({ data: [] }));

// Carousel Component with light theme - Responsive
const Carousel = ({ children, itemWidth = 280 }) => {
  const carouselRef = useRef(null);

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = itemWidth + 16;
      const newScrollLeft = carouselRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      carouselRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="relative group">
      <button
        onClick={() => scrollCarousel('left')}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 text-gray-700 p-2 sm:p-3 rounded-full shadow-lg border border-gray-200 transition-all hover:scale-110 -ml-2 sm:-ml-4 opacity-0 group-hover:opacity-100 sm:opacity-100"
        data-testid="carousel-prev"
      >
        <ChevronLeft className="w-4 h-4 sm:w-6 sm:h-6" />
      </button>
      
      <button
        onClick={() => scrollCarousel('right')}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 text-gray-700 p-2 sm:p-3 rounded-full shadow-lg border border-gray-200 transition-all hover:scale-110 -mr-2 sm:-mr-4 opacity-0 group-hover:opacity-100 sm:opacity-100"
        data-testid="carousel-next"
      >
        <ChevronRight className="w-4 h-4 sm:w-6 sm:h-6" />
      </button>

      <div 
        ref={carouselRef}
        className="flex gap-4 sm:gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-4 px-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {children}
      </div>
    </div>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [trendingCourses, setTrendingCourses] = useState([]);
  const [premiumClients, setPremiumClients] = useState([]);
  const [guests, setGuests] = useState([]);
  const [offres, setOffres] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [jewelryWatchProducts, setJewelryWatchProducts] = useState([]);
  const [bestProducts, setBestProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter state
  const [jobFilters, setJobFilters] = useState({
    type: '',
    location: '',
    enterprise: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Application Modal state
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [userDocuments, setUserDocuments] = useState([]);
  const [applyForm, setApplyForm] = useState({
    resume_url: '',
    cover_letter: ''
  });
  const [applying, setApplying] = useState(false);
  
  // Training purchase state
  const [purchasingTraining, setPurchasingTraining] = useState(null);
  const [purchasingVideo, setPurchasingVideo] = useState(null);
  // State pour afficher tous les produits ou non
  const [showAllProducts, setShowAllProducts] = useState(false);

  // Filter enterprises with real photos only (no unsplash/default images)
  const hasRealPhoto = (enterprise) => {
    const coverImage = enterprise.cover_image || '';
    // Exclude enterprises with no image, unsplash images, or placeholder images
    if (!coverImage) return false;
    if (coverImage.includes('unsplash.com')) return false;
    if (coverImage.includes('placeholder')) return false;
    if (coverImage.includes('default')) return false;
    // Keep only real uploaded images (usually from backend uploads)
    return coverImage.includes('/api/uploads') || coverImage.includes('titelli') || coverImage.includes('render');
  };

  // Sort enterprises by profile completeness
  const sortByProfileCompleteness = (enterprises) => {
    return [...enterprises].sort((a, b) => {
      const getScore = (e) => {
        let score = 0;
        if (hasRealPhoto(e)) score += 5; // Priority to real photos
        if (e.logo) score += 2;
        if (e.description && e.description.length > 50) score += 2;
        if (e.slogan) score += 1;
        if (e.rating > 0) score += 2;
        if (e.review_count > 0) score += 1;
        if (e.is_certified) score += 2;
        if (e.is_labeled) score += 2;
        if (e.is_premium) score += 2;
        return score;
      };
      return getScore(b) - getScore(a);
    });
  };

useEffect(() => {
  const fetchData = async () => {
    try {
      const [
        guestRes,
        offreRes,
        prodCatRes,
        servCatRes,
        jobsRes,
        trainingsRes,
        jewelryProductsRes,
        videoOffersRes,
        videoCoursesRes,
        premiumClientsRes
      ] = await Promise.all([
 loadSafe(() => featuredAPI.guests()),
loadSafe(() => featuredAPI.offres()),
loadSafe(() => categoryAPI.products()),
loadSafe(() => categoryAPI.services()),

        jobsAPI.listAll().catch(() => ({ data: [] })),

        trainingsAPI.listAll({ limit: 6 }).catch(() => ({ data: [] })),

        servicesProductsAPI
          .list({ type: 'product', limit: 50 })
          .catch(() => ({ data: [] })),

        // VIDEOS X -> collection videosx via GET /videos
        videosAPI.listAll({ limit: 12 }).catch((error) => {
          console.error('❌ Erreur GET /videos:', error);
          return { data: [] };
        }),

        // Autres cours vidéo
        featuredAPI.videoCourses(10).catch(() => ({ data: [] })),

        // Clients premium
        featuredAPI.premiumClients(12).catch(() => ({ data: [] }))
      ]);

      // =========================================================
      // GUESTS
      // =========================================================

      const guestData = (guestRes.data || [])
        .filter(hasRealPhoto)
        .sort((a, b) =>
          (a.business_name || a.name || '').localeCompare(
            b.business_name || b.name || ''
          )
        );

      setGuests(guestData);

      // =========================================================
      // VIDEOS
      // =========================================================

      setVideos(videoOffersRes.data || []);

      setTrendingCourses(videoCoursesRes.data || []);

      setPremiumClients(premiumClientsRes.data || []);

      // =========================================================
      // OFFRES / SERVICES
      // =========================================================

      const SERVICE_PRIORITY_CATEGORIES = [
        'restaurant',
        'restauration',
        'traiteur',
        'menage',
        'nettoyage',
        'maison',
        'personnel',
        'beaute',
        'esthetique',
        'spa',
        'massage',
        'soin',
        'coiffure',
        'coiffeur',
        'sport',
        'fitness',
        'coach',
        'yoga',
        'loisir',
        'activite',
        'animation',
        'sante',
        'medical',
        'therapie',
        'immobilier',
        'demenagement',
        'securite',
        'gardiennage'
      ];

      const sortedOffres = (offreRes.data || []).sort((a, b) => {
        const catA = (a.category || '').toLowerCase();
        const catB = (b.category || '').toLowerCase();

        const indexA = SERVICE_PRIORITY_CATEGORIES.findIndex(
          p => catA.includes(p) || p.includes(catA)
        );

        const indexB = SERVICE_PRIORITY_CATEGORIES.findIndex(
          p => catB.includes(p) || p.includes(catB)
        );

        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }

        if (indexA !== -1) {
          return -1;
        }

        if (indexB !== -1) {
          return 1;
        }

        return (a.title || a.name || '').localeCompare(
          b.title || b.name || ''
        );
      });

      setOffres(sortedOffres);

      // =========================================================
      // CATEGORIES PRODUITS
      // =========================================================

      setProductCategories(prodCatRes.data || []);

      // =========================================================
      // CATEGORIES SERVICES
      // =========================================================

      const SERVICE_CAT_PRIORITY = [
        'restauration',
        'restaurant',
        'traiteur',
        'menage',
        'nettoyage',
        'maison',
        'beaute',
        'esthetique',
        'spa',
        'coiffure',
        'sport',
        'fitness',
        'loisirs',
        'activites',
        'sante',
        'medical',
        'immobilier',
        'securite'
      ];

      const sortedServiceCats = (servCatRes.data || []).sort((a, b) => {
        const idA = (a.id || a.name || '').toLowerCase();
        const idB = (b.id || b.name || '').toLowerCase();

        const indexA = SERVICE_CAT_PRIORITY.findIndex(
          p => idA.includes(p) || p.includes(idA)
        );

        const indexB = SERVICE_CAT_PRIORITY.findIndex(
          p => idB.includes(p) || p.includes(idB)
        );

        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }

        if (indexA !== -1) {
          return -1;
        }

        if (indexB !== -1) {
          return 1;
        }

        return (a.name || '').localeCompare(b.name || '');
      });

      setServiceCategories(sortedServiceCats);

      // =========================================================
      // JOBS
      // =========================================================

      const jobsData = jobsRes.data || [];

      setJobs(jobsData);
      setFilteredJobs(jobsData);

      // =========================================================
      // TRAININGS
      // =========================================================

      setTrainings(trainingsRes.data || []);

      // =========================================================
      // PRODUITS
      // =========================================================

      const allProducts =
        jewelryProductsRes.data?.items ||
        jewelryProductsRes.data ||
        [];

      // =========================================================
      // BIJOUX / MONTRES
      // =========================================================

      const jewelryProducts = allProducts.filter(p => {
        const cat = (p.category || '').toLowerCase();
        const name = (p.name || '').toLowerCase();

        return (
          cat.includes('bijou') ||
          cat.includes('montre') ||
          cat.includes('horlog') ||
          cat.includes('joaill') ||
          name.includes('montre') ||
          name.includes('bijou')
        );
      });

      setJewelryWatchProducts(jewelryProducts);

      // =========================================================
      // MEILLEURS PRODUITS
      // =========================================================

      const productsWithImages = allProducts.filter(p => {
        const images = p.images || [];

        const hasImage =
          images.length > 0 &&
          images[0] &&
          !images[0].includes('placeholder');

        const hasPrice = p.price && p.price > 0;

        return hasImage && hasPrice;
      });

      const PRODUCT_PRIORITY_CATEGORIES = [
        // Restaurant / Alimentaire
        'courses_alimentaires',
        'alimentaire',
        'restaurant',
        'cuisine',

        // Maison
        'ameublement_deco',
        'electromenager',
        'bricolage_jardinage',
        'maison',

        // Beauté
        'maquillage_beaute',
        'soins',
        'cosmetique',
        'cosmétique',

        // Coiffure
        'coiffure',

        // Sport
        'sport',

        // Loisirs
        'loisirs',
        'voyages',

        // Santé
        'sante',
        'medical',

        // Immobilier
        'immobilier',

        // Sécurité
        'securite',

        // Autres
        'vetements_mode',
        'enfant',
        'electronique',
        'automobiles'
      ];

      const sortedProducts = productsWithImages.sort((a, b) => {
        const catA = (a.category || '').toLowerCase();
        const catB = (b.category || '').toLowerCase();

        const indexA = PRODUCT_PRIORITY_CATEGORIES.findIndex(
          p => catA.includes(p) || p.includes(catA)
        );

        const indexB = PRODUCT_PRIORITY_CATEGORIES.findIndex(
          p => catB.includes(p) || p.includes(catB)
        );

        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }

        if (indexA !== -1) {
          return -1;
        }

        if (indexB !== -1) {
          return 1;
        }

        return (a.name || '').localeCompare(b.name || '');
      });

      setBestProducts(sortedProducts.slice(0, 20));

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, []);
  // Apply filters
  useEffect(() => {
    let result = [...jobs];
    
    if (jobFilters.type) {
      result = result.filter(job => job.type === jobFilters.type);
    }
    
    if (jobFilters.location) {
      result = result.filter(job => 
        (job.location || '').toLowerCase().includes(jobFilters.location.toLowerCase())
      );
    }
    
    if (jobFilters.enterprise) {
      result = result.filter(job => 
        (job.enterprise_name || '').toLowerCase().includes(jobFilters.enterprise.toLowerCase())
      );
    }
    
    setFilteredJobs(result);
  }, [jobFilters, jobs]);
  
  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };
  
  // Open apply modal
  const handleApplyClick = async (e, job) => {
    e.preventDefault();
    e.stopPropagation();
    
    const token = localStorage.getItem('titelli_token');
    if (!token) {
      toast.error('Connectez-vous pour postuler');
      return;
    }
    
    setSelectedJob(job);
    setApplyForm({ resume_url: '', cover_letter: '' });
    
    try {
      const res = await clientDocumentsAPI.list();
      const allDocs = res.data?.documents || res.data || [];
      const cvDocs = allDocs.filter(d => d.category === 'cv' || d.category === 'general' || d.file_type?.includes('pdf'));
      setUserDocuments(cvDocs.length > 0 ? cvDocs : allDocs);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setUserDocuments([]);
    }
    
    setShowApplyModal(true);
  };
  
  // Submit application
  const handleSubmitApplication = async () => {
    if (!applyForm.resume_url) {
      toast.error('Veuillez sélectionner un CV');
      return;
    }
    
    setApplying(true);
    try {
      await jobsAPI.apply(selectedJob.id, {
        resume_url: applyForm.resume_url,
        cover_letter: applyForm.cover_letter
      });
      toast.success('Candidature envoyée avec succès !');
      setShowApplyModal(false);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de l\'envoi';
      toast.error(msg);
    } finally {
      setApplying(false);
    }
  };

  // Handle training purchase
  const handlePurchaseTraining = async (training) => {
    const token = localStorage.getItem('titelli_token');
    if (!token) {
      toast.error('Connectez-vous pour acheter une formation');
      navigate('/auth');
      return;
    }
    
    setPurchasingTraining(training.id);
    try {
      const res = await trainingsAPI.purchase(training.id);
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de l\'achat';
      toast.error(msg);
    } finally {
      setPurchasingTraining(null);
    }
  };

  // Video state for panoramic hero
  const videoRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const [isVideoMuted, setIsVideoMuted] = useState(true);

  const toggleVideoPlay = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  const toggleVideoMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isVideoMuted;
      setIsVideoMuted(!isVideoMuted);
    }
  };

  const handlePurchaseVideo = async (video) => {
    const token = localStorage.getItem('titelli_token');
    if (!token) {
      toast.error('Connectez-vous pour acheter une vidéo');
      navigate('/auth');
      return;
    }
    
    setPurchasingVideo(video.id);
    try {
      const res = await videosAPI.purchase(video.id);
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de l\'achat';
      toast.error(msg);
    } finally {
      setPurchasingVideo(null);
    }
  };





  const panoramicVideoUrl = `head.mp4`;
  const heroImage = 'https://lexata.ch/static/img/hero1.jpg';
  const heroImageMobile = 'https://lexata.ch/static/img/moi9.png'; 




  const heroNavCategories = [
    { id: 'services', label: 'Services', path: '/services', icon: Sparkles },
    { id: 'produits', label: 'Produits', path: '/products', icon: Gift },
    { id: 'certifies', label: 'Certifiés', path: '/certifies', icon: CheckCircle },
    { id: 'guests', label: 'Guests', path: '/guests', icon: Users },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section with Panoramic Video - No gap with navbar */}
      <section className="relative h-[70vh] overflow-hidden pt-14 lg:" data-testid="hero-section">
        {/* Video Background */}
<div className="absolute inset-0">
  <picture>
    {/* Mobile : jusqu'à 767px */}
    <source media="(max-width: 767px)" srcSet={heroImageMobile} />
    {/* Desktop : par défaut */}
    <img
      src={heroImage}
      alt="Lausanne"
      className="absolute inset-0 w-full h-full object-cover -z-10"
    />
  </picture>
</div>

        {/* Hero Content - Title aligned left */}
      <div className="absolute inset-0 flex flex-col justify-center px-4 md:px-8">
          <div className="max-w-7xl mx-auto w-full">
            <h1 className="text-4xl bg-white sm:text-5xl lg:text-7xl font-bold text-black mb-8 animate-fade-in drop-shadow-lg mt-20 sm:mt-40 lg:mt-72" style={{ fontFamily: 'inter', textAlign: 'center' }}>
                  Ts<span className="p-3 text-pink-500/80">Lexata</span>
              
            </h1>

            {/* Category Buttons - 2 rows - Same size on mobile */}
            <div className="flex flex-col items-center gap-2 animate-fade-in stagger-2 px-2 -mt-4 sm:mt-0">


              <div className="grid grid-cols-2 gap-2 w-full max-w-md justify-center mx-auto">
                {[
                  { label: 'Vidéos', path: '/video-pub' },
                  { label: 'Gallerie', path: '/services' },

                ].map((cat) => (
                  <Link
                    key={cat.label}
                    to={cat.path}
                    className="p-3 bg-pink-500/80  border border-white/20 rounded-xl text-white text-xs sm:text-sm font-medium hover:bg-white hover:text-black transition-all duration-300 text-center"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {cat.label}
                  </Link>
                ))}
              </div>



              
              <div className="grid grid-cols-4 gap-2 w-full max-w-md">
                {[
                  { label: 'Telegram', path: 'https://t.me/+fRc78jVH1RwyZmY0', className: 'bg-blue-500' },
                  { label: 'Mym', path: '/', className: 'bg-blue-500'  },
                  { label: 'RedPeach', path: 'https://redpeach.com/fr/tslexata', className: 'bg-orange-500'  },
                  { label: 'Pornhub', path: 'https://fr.pornhub.com/model/ts-lexata' , className: 'bg-yellow-500' },

                ].map((cat) => (
                  <Link
                    key={cat.label}
                    to={cat.path}
                    className={`p-3 border border-white/20 rounded-xl text-white text-xs sm:text-sm font-medium hover:bg-white hover:text-black transition-all duration-300 text-center ${cat.className || 'bg-pink-500/80'}`}
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {cat.label}
                  </Link>
                ))}
              </div>


            </div>
          </div>
        </div>




      </section>


      {/* Search Bar Section - Under Video */}
      <section className="py-4 sm:py-6 bg-white" data-testid="search-section">
        <div className="max-w-2xl mx-auto px-4">
         <Link to="/" className="text-amber-400 hover:text-amber-300 hidden sm:inline-block" data-testid="logo-link">
                <img 
                    src="/logo_titelli.png" 
                    alt="Titelli"
                    className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                  />
            </Link>


          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une image, vidéo, abonnement..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-20 py-2.5 bg-gray-50 border border-gray-200 rounded-full text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#D4627F] focus:bg-white transition-all"
              data-testid="home-search-input"
            />
            <button 
              type="submit"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-[#D4627F] text-white px-4 py-1.5 rounded-full text-xs font-medium hover:bg-[#D4627F]/90 transition-colors"
            >
              Rechercher
            </button>
          </form>
        </div>
      </section>

 <LiveStreamSection />

      {/* Mes vidéos personnalisées - remplace les catégories avec vidéo */}
      <section className="py-6 sm:py-10 bg-white" data-testid="video-offers-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Mes vidéos  <span className="text-pink-500">X</span>
          </h2>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-[320px] w-full bg-rose-50 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : videos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {videos.map((video) => (
                <VideoCourseCard
                  key={video.id}
                  course={video}
                  onBuy={handlePurchaseVideo}
                  buying={purchasingVideo === video.id}
                  trackViews
                />
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-left py-8 text-sm">Aucune vidéo disponible pour le moment</p>
          )}
        </div>
      </section>

      {/* Services Section - Grid 4 columns - HIDDEN */}
      <section className="py-8 sm:py-16 bg-gray-50" data-testid="services-section" style={{ display: 'none' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-6 sm:mb-8">
            <div>
              <h2 className="text-lg sm:text-2xl md:text-3xl font-semibold text-gray-900">
                Les meilleurs services de votre région
              </h2>
            </div>
            <Link to="/services" className="hidden md:flex items-center gap-2 text-[#D4627F] hover:text-[#E58AA0] font-medium transition-colors" data-testid="view-all-services">
              Voir tout
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* Service Category Tabs - Transparent style */}
          <div className="flex gap-2 sm:gap-3 mb-6 sm:mb-8 overflow-x-auto scrollbar-hide pb-2" style={{ scrollbarWidth: 'none' }}>
            {serviceCategories.slice(0, 6).map((cat) => (
              <Link
                key={cat.id}
                to={`/services?category=${cat.id}`}
                className="px-3 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm text-gray-600 hover:text-[#D4627F] transition-all whitespace-nowrap flex-shrink-0"
                data-testid={`service-cat-${cat.id}`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Service Cards Grid - 5 columns */}
          {offres.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {offres.slice(0, 20).map((item, index) => (
                <div key={item.id}>
                  <ServiceProductCard item={item} />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map((i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
                  <div className="h-40 sm:h-56 bg-gray-100 animate-pulse" />
                  <div className="p-4 sm:p-5 space-y-3">
                    <div className="h-5 sm:h-6 bg-gray-100 rounded animate-pulse" />
                    <div className="h-4 bg-gray-100 rounded w-3/4 animate-pulse" />
                    <div className="h-6 sm:h-8 bg-gray-100 rounded w-1/3 animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          )}

          <Link to="/services" className="md:hidden flex items-center justify-center gap-2 mt-4 sm:mt-8 text-[#D4627F] font-medium text-sm">
            Voir tous les services
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      

      {/* Premium - clients premium mis en avant */}
      {premiumClients.length > 0 && (
        <section className="py-8 sm:py-16 bg-rose-50/60" data-testid="premium-section">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-2xl md:text-3xl font-semibold text-gray-900">
                Derniers Soumis Premium
              </h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
              {premiumClients.map((client, index) => (
                <PremiumClientCard key={`${client.pseudo}-${index}`} client={client} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Job Offers Section - Carousel */}
      <section className="py-8 sm:py-16 bg-gray-50" data-testid="jobs-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div>
              <h2 className="text-lg sm:text-2xl md:text-3xl font-semibold text-gray-900">
                Offres de tournage
              </h2>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm transition-colors ${showFilters ? 'bg-[#D4627F] text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}
                data-testid="jobs-filter-btn"
              >
                <Filter className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Filtres</span>
              </button>
              <Link to="/emplois" className="hidden md:flex items-center gap-2 text-[#D4627F] hover:text-[#E58AA0] font-medium transition-colors">
                Voir tout
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
          
          {/* Filters */}
          {showFilters && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-5 bg-white rounded-xl border border-gray-200 shadow-sm animate-fade-in" data-testid="jobs-filters">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm text-gray-600 mb-1.5 sm:mb-2">Type</label>
                  <select 
                    value={jobFilters.type}
                    onChange={(e) => setJobFilters({...jobFilters, type: e.target.value})}
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-2 sm:px-4 py-2 text-xs sm:text-sm text-gray-900 focus:border-[#D4627F] outline-none"
                    data-testid="jobs-filter-type"
                  >
                    <option value="">Tous</option>
                    <option value="CDI">CDI</option>
                    <option value="CDD">CDD</option>
                    <option value="Stage">Stage</option>
                    <option value="Freelance">Freelance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm text-gray-600 mb-1.5 sm:mb-2">Ville</label>
                  <input 
                    type="text"
                    value={jobFilters.location}
                    onChange={(e) => setJobFilters({...jobFilters, location: e.target.value})}
                    placeholder="Lausanne..."
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-2 sm:px-4 py-2 text-xs sm:text-sm text-gray-900 placeholder-gray-500 focus:border-[#D4627F] outline-none"
                    data-testid="jobs-filter-location"
                  />
                </div>
                <div className="col-span-2 lg:col-span-1">
                  <label className="block text-xs sm:text-sm text-gray-600 mb-1.5 sm:mb-2">Entreprise</label>
                  <input 
                    type="text"
                    value={jobFilters.enterprise}
                    onChange={(e) => setJobFilters({...jobFilters, enterprise: e.target.value})}
                    placeholder="Nom..."
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-2 sm:px-4 py-2 text-xs sm:text-sm text-gray-900 placeholder-gray-500 focus:border-[#D4627F] outline-none"
                    data-testid="jobs-filter-enterprise"
                  />
                </div>
                <div className="flex items-end col-span-2 lg:col-span-1">
                  <button 
                    onClick={() => setJobFilters({ type: '', location: '', enterprise: '' })}
                    className="w-full px-3 sm:px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs sm:text-sm text-gray-700 hover:text-gray-900 transition-colors"
                  >
                    Réinitialiser
                  </button>
                </div>
              </div>
              {filteredJobs.length !== jobs.length && (
                <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-[#D4627F]">{filteredJobs.length} résultat(s) sur {jobs.length}</p>
              )}
            </div>
          )}

          {filteredJobs.length > 0 ? (
            <Carousel itemWidth={280}>
              {filteredJobs.slice(0, 9).map((job, index) => (
                <div 
                  key={job.id} 
                  className="flex-shrink-0 w-[280px] sm:w-[320px] bg-white rounded-xl p-3 sm:p-5 border border-gray-200 shadow-sm hover:border-[#D4627F]/30 hover:shadow-md transition-all"
                  data-testid={`job-card-${job.id}`}
                >
                  <Link to={`/emploi/${job.id}`} className="block">
                    <div className="flex items-start justify-between mb-2 sm:mb-3 gap-2">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#D4627F]/10 flex items-center justify-center flex-shrink-0">
                          <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4627F]" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-gray-900 font-semibold text-xs sm:text-sm line-clamp-1">{job.title}</h3>
                          <p className="text-[#E58AA0] text-xs sm:text-sm font-medium line-clamp-1">{job.enterprise_name || 'Entreprise'}</p>
                        </div>
                      </div>
                      <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full flex-shrink-0 ${
                        job.type === 'CDI' ? 'bg-green-100 text-green-700' :
                        job.type === 'CDD' ? 'bg-orange-100 text-orange-700' :
                        job.type === 'Stage' ? 'bg-purple-100 text-purple-700' :
                        job.type === 'Freelance' ? 'bg-cyan-100 text-cyan-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {job.type || 'CDI'}
                      </span>
                    </div>
                    <p className="text-gray-600 text-xs sm:text-sm line-clamp-2 mb-3 sm:mb-4">{job.description}</p>
                    <div className="flex items-center gap-3 sm:gap-4 text-[10px] sm:text-xs text-gray-500 mb-3 sm:mb-4">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {job.location || 'Lausanne'}
                      </span>
                    </div>
                  </Link>
                  <button
                    onClick={(e) => handleApplyClick(e, job)}
                    className="w-full bg-[#D4627F] hover:bg-[#D4627F]/80 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center justify-center gap-1.5 sm:gap-2"
                    data-testid={`job-apply-btn-${job.id}`}
                  >
                    <Send className="w-3 h-3 sm:w-4 sm:h-4" />
                    Postuler
                  </button>
                </div>
              ))}
            </Carousel>
          ) : (
            <div className="text-center py-8 sm:py-12">
              <Briefcase className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
              <p className="text-gray-600 text-sm">
                {jobs.length === 0 ? "Aucune offre pour le moment" : "Aucune offre ne correspond"}
              </p>
            </div>
          )}

          <Link to="/emplois" className="md:hidden flex items-center justify-center gap-2 mt-4 sm:mt-8 text-[#D4627F] font-medium text-sm">
            Voir toutes les offres
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Formations Section - Carousel */}
      {trainings.length > 0 && (
        <section className="py-8 sm:py-16 bg-white" data-testid="trainings-section">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-start sm:items-center justify-between mb-6 sm:mb-8 gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="p-2 sm:p-3 rounded-xl bg-purple-100 flex-shrink-0">
                  <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Formations Soumis Privé
                  </h2>
                  <p className="text-gray-600 mt-0.5 sm:mt-1 text-xs sm:text-sm">Formation pour devenir mon soumis privé a domicile.</p>
                </div>
              </div>
            </div>

            <Carousel itemWidth={280}>
              {trainings.map((training) => (
                <div 
                  key={training.id} 
                  className="flex-shrink-0 w-[280px] sm:w-[320px] bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-all"
                  data-testid={`training-card-${training.id}`}
                >
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                      <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${
                        training.training_type === 'online' 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {training.training_type === 'online' ? 'En ligne' : 'Présentiel'}
                      </span>
                      <span className="px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium bg-gray-100 text-gray-700 line-clamp-1">
                        {training.category}
                      </span>
                    </div>
                    
                    <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-1.5 sm:mb-2 line-clamp-2">{training.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mb-2 sm:mb-3">{training.description}</p>
                    
                    <div className="flex items-center gap-2 mb-3 sm:mb-4">
                      {training.enterprise_logo ? (
                        <img 
                          src={training.enterprise_logo.startsWith('http') ? training.enterprise_logo : `${process.env.REACT_APP_BACKEND_URL}${training.enterprise_logo}`}
                          alt={training.enterprise_name}
                          className="w-6 h-6 sm:w-8 sm:h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-[#D4627F]/20 flex items-center justify-center text-[10px] sm:text-xs text-[#D4627F] font-bold">
                          {training.enterprise_name?.[0]}
                        </div>
                      )}
                      <span className="text-xs sm:text-sm text-[#E58AA0] font-medium line-clamp-1">{training.enterprise_name}</span>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 sm:gap-3 text-[10px] sm:text-xs text-gray-500 mb-3 sm:mb-4">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {training.duration}
                      </span>
                      {training.certificate && (
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="w-3 h-3" />
                          Certificat
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between pt-3 sm:pt-4 border-t border-gray-200">
                      <div>
                        <p className="text-lg sm:text-2xl font-bold text-gray-900">{training.price} <span className="text-xs sm:text-sm font-normal text-gray-500">CHF</span></p>
                      </div>
                      <button
                        onClick={() => handlePurchaseTraining(training)}
                        disabled={purchasingTraining === training.id}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#D4627F] text-white rounded-lg text-xs sm:text-sm hover:bg-[#D4627F]/80 transition-colors disabled:opacity-50 flex items-center gap-1.5 sm:gap-2"
                        data-testid={`buy-training-${training.id}`}
                      >
                        {purchasingTraining === training.id ? (
                          <>
                            <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            <span className="hidden sm:inline">Achat...</span>
                          </>
                        ) : (
                          "S'inscrire"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </Carousel>
          </div>
        </section>
      )}
      
      {/* Apply Modal */}
      {showApplyModal && selectedJob && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="apply-modal">
          <div className="bg-[#0A0A0A] rounded-2xl w-full max-w-lg border border-white/10 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Postuler à l'offre</h2>
                <button onClick={() => setShowApplyModal(false)} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="bg-white/5 rounded-xl p-4">
                <h3 className="text-white font-semibold">{selectedJob.title}</h3>
                <p className="text-[#E58AA0] text-sm">{selectedJob.enterprise_name}</p>
                <div className="flex items-center gap-4 text-xs text-gray-400 mt-2">
                  <span>{selectedJob.type}</span>
                  <span>{selectedJob.location || 'Lausanne'}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  <FileText className="w-4 h-4 inline mr-2" />
                  Sélectionnez votre CV *
                </label>
                {userDocuments.length > 0 ? (
                  <div className="space-y-2">
                    {userDocuments.map((doc) => (
                      <label 
                        key={doc.id} 
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          applyForm.resume_url === doc.url 
                            ? 'bg-[#D4627F]/20 border border-[#D4627F]' 
                            : 'bg-white/5 border border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <input
                          type="radio"
                          name="resume"
                          value={doc.url}
                          checked={applyForm.resume_url === doc.url}
                          onChange={(e) => setApplyForm({...applyForm, resume_url: e.target.value})}
                          className="accent-[#D4627F]"
                        />
                        <FileText className="w-5 h-5 text-[#D4627F]" />
                        <div className="flex-1">
                          <span className="text-white text-sm">{doc.name}</span>
                          <span className="text-xs text-gray-500 ml-2">({doc.category})</span>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="text-center p-6 bg-white/5 rounded-xl">
                    <FileText className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm mb-3">Vous n'avez pas encore de CV enregistré</p>
                    <Link 
                      to={`/dashboard/client?tab=documents&returnToJob=${selectedJob.id}`}
                      className="text-[#D4627F] hover:underline text-sm"
                      onClick={() => setShowApplyModal(false)}
                    >
                      Ajouter un CV dans mon espace
                    </Link>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Lettre de motivation (optionnel)</label>
                <textarea
                  value={applyForm.cover_letter}
                  onChange={(e) => setApplyForm({...applyForm, cover_letter: e.target.value})}
                  placeholder="Présentez-vous brièvement et expliquez votre motivation..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-[#D4627F] outline-none h-32 resize-none"
                  data-testid="apply-cover-letter"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowApplyModal(false)}
                  className="flex-1 px-4 py-3 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSubmitApplication}
                  disabled={applying || !applyForm.resume_url}
                  className="flex-1 px-4 py-3 rounded-xl bg-[#D4627F] text-white hover:bg-[#D4627F]/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="apply-submit-btn"
                >
                  {applying ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Envoyer ma candidature
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scrolling Reviews Section */}
      <ScrollingReviews 
        title="Ce que mes soumis disent de moi"
        speed={35}
      />

   

      {/* CTA Section */}
    </div>
  );
};

export default HomePage;
